/**
 * ChaseCameraRig - the third-person view: a passive chase camera derived from
 * the player camera, plus the LocalAvatar self-pod.
 *
 * The player UniversalCamera stays untouched as the invisible "player" - it
 * keeps ALL input (pointer-lock look, WASD, thumbstick, click-to-walk,
 * presenter follow, tour), the collision ellipsoid, gravity, and the
 * multiplayer identity (its position/yaw are what event 1 broadcasts). The
 * chase camera only renders: no attachControl, no collisions, its pose is
 * recomputed every frame from the player pose.
 *
 * Babylon's Scene.render() calls camera.update() - the ONLY consumer of the
 * cameraDirection/cameraRotation input buffers and the only place gravity +
 * ellipsoid collisions apply - on the ACTIVE camera alone. So while the chase
 * camera is active, the rig drives playerCamera.update() manually each tick;
 * movement then behaves bit-identically to first person. The tick runs in the
 * CAMERA_RIG update band: after INPUT steering has written this frame's
 * movement and before DEFAULT consumers such as interaction hover and hotspot
 * projection.
 *
 * Camera-pose hijackers (drone view, arrival cinematic) suspend the rig for
 * their lifetime via suspend()/resume() - rendering falls back to the player
 * camera and the self-pod hides, whatever the stored setting says.
 */

import { UniversalCamera, Vector3, Ray } from '@babylonjs/core';
import { LEGACY_MAX_Z } from '../utils/drawDistance';
import { UPDATE_ORDER, UpdateManager } from '../utils/UpdateManager';
import { createErrorScope, ErrorCategory } from '../utils/sceneErrorHandler';
import { getCameraView, onCameraViewChange } from '../input/cameraView.js';
import { CHASE_DEFAULTS, computeChaseGeometry, resolveBoomLength } from './chaseCameraMath.js';
import { LocalAvatar } from './LocalAvatar.js';
import { getActiveMeshFreezer } from '../utils/activeMeshFreezer.js';

const errorHandler = createErrorScope('ChaseCameraRig', ErrorCategory.ENTITY);

/** @type {WeakMap<object, ChaseCameraRig>} */
const rigRegistry = new WeakMap();

/**
 * The scene's chase rig, if one was constructed (solo harnesses and tests
 * have none).
 * @param {import('@babylonjs/core').Scene | null | undefined} scene
 * @returns {ChaseCameraRig | null}
 */
export function getChaseCameraRig(scene) {
  return (scene && rigRegistry.get(scene)) || null;
}

export class ChaseCameraRig {
  /**
   * @param {import('@babylonjs/core').Scene} scene
   * @param {import('@babylonjs/core').UniversalCamera} playerCamera
   */
  constructor(scene, playerCamera) {
    this.scene = scene;
    this.playerCamera = playerCamera;
    this._disposed = false;
    /** @type {Set<string>} */
    this._suspendReasons = new Set();
    /** @type {'first'|'third'} */
    this._view = 'first';

    const cam = new UniversalCamera('chaseCam', playerCamera.position.clone(), scene);
    cam.rotation.copyFrom(playerCamera.rotation);
    cam.checkCollisions = false;
    cam.applyGravity = false;
    cam.inertia = 0;
    cam.minZ = 0.3;
    // Construction default only - DrawDistanceController retunes maxZ per
    // quality tier x level on both renderable cameras together.
    cam.maxZ = LEGACY_MAX_Z;
    this.chaseCamera = cam;

    this.localAvatar = new LocalAvatar(scene);

    // Scratch objects reused every tick - no per-frame allocation.
    this._ray = new Ray(new Vector3(), new Vector3(0, 0, 1), CHASE_DEFAULTS.boom);

    this._updateId = UpdateManager.getInstance(scene).register(
      'ChaseCameraRig',
      () => this._tick(),
      { priority: UPDATE_ORDER.CAMERA_RIG },
    );
    this._unsubscribeView = onCameraViewChange((view) => this._applyView(view));
    this._disposeObserver = scene.onDisposeObservable.add(() => this.dispose());

    rigRegistry.set(scene, this);
    this._applyView(getCameraView());
  }

  /** @returns {boolean} */
  isSuspended() {
    return this._suspendReasons.size > 0;
  }

  /**
   * Force first-person rendering while another system owns the camera pose
   * (drone view, arrival cinematic). Reentrant per reason.
   * @param {string} reason
   */
  suspend(reason) {
    if (this._disposed) return;
    this._suspendReasons.add(reason);
    if (this.scene.activeCamera === this.chaseCamera) {
      this.scene.activeCamera = this.playerCamera;
    }
    this.localAvatar.setEnabled(false);
  }

  /**
   * Release a suspend reason; when the last one clears, the stored view
   * preference is re-applied.
   * @param {string} reason
   */
  resume(reason) {
    if (this._disposed) return;
    this._suspendReasons.delete(reason);
    if (!this._suspendReasons.size) this._applyView(getCameraView());
  }

  /** @param {'first'|'third'} view */
  _applyView(view) {
    this._view = view;
    if (this._disposed || this.isSuspended()) return;
    if (view === 'third') {
      // Place the chase camera BEFORE it becomes active so the switch never
      // renders one frame from a stale pose. No playerCamera.update() here -
      // the player camera is still active this frame and Scene.render()
      // already drives it.
      this._placeChase();
      this.scene.activeCamera = this.chaseCamera;
      this.localAvatar.sync(this.playerCamera.position, this.playerCamera.rotation.y);
      this.localAvatar.setEnabled(true);
      getActiveMeshFreezer(this.scene)?.requestRefreeze('third-person');
    } else {
      this.scene.activeCamera = this.playerCamera;
      this.localAvatar.setEnabled(false);
    }
  }

  _tick() {
    if (this._disposed || this._view !== 'third' || this._suspendReasons.size) return;
    try {
      this.playerCamera.update();
      this._placeChase();
      this.localAvatar.sync(this.playerCamera.position, this.playerCamera.rotation.y);
    } catch (e) {
      errorHandler.warn('tick', `chase update failed: ${e?.message}`);
    }
  }

  /** Recompute the chase camera pose from the current player pose. */
  _placeChase() {
    const p = this.playerCamera;
    const geo = computeChaseGeometry({
      x: p.position.x,
      y: p.position.y,
      z: p.position.z,
      yaw: p.rotation.y,
      pitch: p.rotation.x,
    });

    // Shorten the boom against anything the player collides with (invisible
    // walls included - they are isPickable=false but checkCollisions=true,
    // which is exactly why this predicate is collision-based). Avatar pods
    // are non-colliding and stay transparent to it.
    this._ray.origin.copyFromFloats(geo.head.x, geo.head.y, geo.head.z);
    this._ray.direction.copyFromFloats(-geo.forward.x, -geo.forward.y, -geo.forward.z);
    this._ray.length = CHASE_DEFAULTS.boom;
    const pick = this.scene.pickWithRay(this._ray, (m) => m.checkCollisions && m.isEnabled());
    const boom = resolveBoomLength(CHASE_DEFAULTS.boom, pick?.hit ? pick.distance : null);

    this.chaseCamera.position.copyFromFloats(
      geo.head.x - geo.forward.x * boom,
      geo.head.y - geo.forward.y * boom,
      geo.head.z - geo.forward.z * boom,
    );
    this.chaseCamera.rotation.set(geo.pitch, p.rotation.y, 0);
  }

  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    UpdateManager.getInstance(this.scene).unregister(this._updateId);
    this._unsubscribeView?.();
    this.scene.onDisposeObservable.remove(this._disposeObserver);
    if (this.scene.activeCamera === this.chaseCamera) {
      this.scene.activeCamera = this.playerCamera;
    }
    this.localAvatar.dispose();
    rigRegistry.delete(this.scene);
  }
}
